import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Loader2, ArrowLeft, ArrowRight, CheckCircle2, ClipboardList, Building2,
  User, Calendar, MessageSquare, AlertTriangle, ShieldCheck, History
} from "lucide-react";
import { toast } from "sonner";
import { eosApi, getAdminKey } from "../../lib/eosApi";
import { useDocMeta } from "../../lib/useDocMeta";

// Story #7 — Engagement Lifecycle. 8-state machine with audit trail.
// States pulled from GET /api/eos/meta so the frontend never drifts from
// the backend truth.

function stateStyle(state) {
  const m = {
    "New":                 { text: "text-sky-300",     bg: "bg-sky-500/10",    border: "border-sky-500/50" },
    "Scoping":             { text: "text-amber-300",   bg: "bg-amber-500/10",  border: "border-amber-500/50" },
    "Proposal / SOW Sent": { text: "text-amber-300",   bg: "bg-amber-500/10",  border: "border-amber-500/50" },
    "Accepted":            { text: "text-sky-300",     bg: "bg-sky-500/10",    border: "border-sky-500/50" },
    "In Progress":         { text: "text-sky-300",     bg: "bg-sky-500/10",    border: "border-sky-500/50" },
    "Founder Review":      { text: "text-amber-300",   bg: "bg-amber-500/10",  border: "border-amber-500/50" },
    "Delivered":           { text: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/50" },
    "Closed":              { text: "text-slate-300",   bg: "bg-slate-500/10",  border: "border-slate-500/50" },
  };
  return m[state] || { text: "text-slate-300", bg: "bg-slate-500/10", border: "border-slate-500/40" };
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function EosEngagement() {
  useDocMeta({ title: "Engagement · EOS", path: "" });
  const { id } = useParams();
  const nav = useNavigate();
  const hasKey = getAdminKey();
  const [eng, setEng] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    if (!hasKey) return;
    try {
      const [e, m] = await Promise.all([
        eosApi().get(`/engagements/${id}`),
        eosApi().get("/meta"),
      ]);
      setEng(e.data); setMeta(m.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Not found");
    }
  };
  useEffect(() => { load(); }, [id, hasKey]);

  if (!hasKey) return <Navigate to="/eos" replace />;

  const transition = async (toState) => {
    setBusy(true);
    try {
      await eosApi().patch(`/engagements/${id}/state`, { to_state: toState, note: note.trim() || null });
      toast.success(`Moved to ${toState}`);
      setNote("");
      await load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Transition failed");
    } finally { setBusy(false); }
  };

  if (err) return (
    <div className="max-w-2xl mx-auto px-6 py-10" data-testid="eos-engagement-error">
      <div className="border border-rose-500/40 bg-rose-500/5 p-4 inline-flex items-start gap-2 mb-4">
        <AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5" />
        <div className="text-sm text-slate-300">{err}</div>
      </div>
      <Link to="/eos/inbox" className="font-mono text-[10px] tracking-widest uppercase text-sky-400">← Back to inbox</Link>
    </div>
  );
  if (!eng || !meta) return <div className="p-10"><Loader2 className="w-4 h-4 animate-spin text-slate-500" /></div>;

  const sr = eng.service_request || {};
  const pursuit = eng.pursuit || {};
  const intake = pursuit.intake || {};
  const es = ((pursuit.report || {}).executive_summary) || {};
  const finalRec = ((pursuit.report || {}).final_recommendation) || {};
  const s = stateStyle(eng.state);
  const allowed = meta.allowed_transitions[eng.state] || [];
  const canClose = eng.state !== "Closed";
  const closeAlreadyAllowed = allowed.includes("Closed");

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-8" data-testid="eos-engagement">
      <Link to="/eos/inbox" className="font-mono text-[10px] tracking-widest uppercase text-slate-500 hover:text-sky-400 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to inbox
      </Link>

      <div className="tick-label text-sky-400 mb-1">/ Engagement</div>
      <h1 className="font-display font-black text-3xl text-slate-100">{sr.opportunity_name || eng.opportunity_name || "Engagement"}</h1>
      <div className="text-slate-400 text-sm mt-1 mb-6 flex items-center gap-3 flex-wrap">
        <span>{sr.company_name || eng.customer_email}</span>
        <span>·</span>
        <span>{sr.agency || eng.agency || "—"}</span>
        {sr.solicitation_number && <><span>·</span><span className="font-mono text-xs">{sr.solicitation_number}</span></>}
      </div>

      {/* State hero */}
      <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)] mb-6">
        <div className="bg-[var(--bg-secondary)] p-4" data-testid="eos-engagement-state">
          <div className="tick-label mb-2">Current State</div>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 border ${s.border} ${s.bg}`}>
            <ShieldCheck className={`w-3.5 h-3.5 ${s.text}`} strokeWidth={2} />
            <span className={`font-mono text-[11px] tracking-[0.2em] uppercase font-bold ${s.text}`} data-testid="eos-engagement-state-label">{eng.state}</span>
          </div>
          <div className="font-mono text-[10px] text-slate-500 mt-2">Updated {fmtWhen(eng.updated_at)}</div>
        </div>
        <div className="bg-[var(--bg-secondary)] p-4">
          <div className="tick-label mb-2">Requested Service</div>
          <div className="text-slate-100 font-mono text-sm">{(sr.requested_service || eng.requested_service || "").replace(/_/g, " ").toUpperCase() || "—"}</div>
          <div className="text-slate-400 text-xs mt-1">Priority: <span className="text-slate-200 font-mono">{sr.priority || eng.priority || "—"}</span></div>
        </div>
        <div className="bg-[var(--bg-secondary)] p-4">
          <div className="tick-label mb-2">Bid Recommendation</div>
          <div className="text-slate-100 text-sm">{finalRec.verdict || "—"}</div>
          <div className="text-slate-400 text-xs mt-1">Analysis Confidence: <span className="text-slate-200 font-mono">{typeof finalRec.analysis_confidence === "number" ? `${finalRec.analysis_confidence}%` : "—"}</span></div>
        </div>
      </div>

      {/* State machine controls */}
      <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5 mb-6" data-testid="eos-engagement-transitions">
        <div className="tick-label text-sky-400 mb-3">/ Transition State</div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note for this transition (audit-logged)…"
          data-testid="eos-engagement-note"
          rows={2}
          className="w-full bg-[var(--bg-primary)] border border-[var(--border-soft)] focus:border-sky-500/70 outline-none text-slate-100 text-sm px-3 py-2 mb-3"
        />
        <div className="flex flex-wrap gap-2">
          {allowed.filter((s) => s !== "Closed").map((toState) => (
            <button
              key={toState}
              disabled={busy}
              onClick={() => transition(toState)}
              data-testid={`eos-engagement-transition-${toState.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-slate-950 font-mono text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-2"
            >
              <ArrowRight className="w-3 h-3" strokeWidth={2.5} /> {toState}
            </button>
          ))}
          {canClose && closeAlreadyAllowed && (
            <button
              disabled={busy}
              onClick={() => transition("Closed")}
              data-testid="eos-engagement-transition-closed"
              className="inline-flex items-center gap-2 border border-rose-500/40 hover:bg-rose-500/10 disabled:opacity-40 text-rose-300 font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2"
            >
              Close Engagement
            </button>
          )}
          {allowed.length === 0 && (
            <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Terminal state — no further transitions.</span>
          )}
        </div>
      </section>

      {/* Two-column layout: pursuit context + audit log */}
      <div className="grid md:grid-cols-2 gap-6">
        <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5">
          <div className="tick-label text-sky-400 mb-3">/ Originating Pursuit</div>
          <KV label="Opportunity" value={intake.opportunity_name || es.opportunity_name || pursuit.document_name} />
          <KV label="Agency" value={intake.agency || es.agency} />
          <KV label="Solicitation #" value={intake.solicitation_number || es.solicitation_number} mono />
          <KV label="Ceiling" value={intake.estimated_ceiling} />
          <KV label="Response Due" value={intake.response_due_date} />
          <KV label="Customer" value={(pursuit.contractor_profile || {}).company_name || eng.customer_email} />
          {pursuit.id && (
            <Link to={`/app/pursuits/${pursuit.id}/brief`} target="_blank" className="mt-3 inline-flex items-center gap-2 border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2">
              <ClipboardList className="w-3 h-3" /> Open Executive Brief
            </Link>
          )}
        </section>

        <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5" data-testid="eos-engagement-audit">
          <div className="tick-label text-sky-400 mb-3 flex items-center gap-1.5"><History className="w-3 h-3" /> Audit Trail</div>
          <ol className="space-y-3">
            {(eng.state_history || []).slice().reverse().map((h, i) => {
              const st = stateStyle(h.to);
              return (
                <li key={i} className="flex gap-3">
                  <div className={`flex-shrink-0 w-1.5 h-1.5 mt-2 rounded-full ${st.bg.replace('bg-', 'bg-').replace('/10','/60')}`} />
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-mono text-[10px] text-slate-500">{fmtWhen(h.at)}</span>
                      <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400">{h.by || "—"}</span>
                    </div>
                    <div className="text-sm text-slate-100 mt-0.5">
                      {h.from ? <><span className="text-slate-500">{h.from}</span> <ArrowRight className="inline w-3 h-3 text-slate-500 mx-1" /></> : null}
                      <span className={`font-medium ${st.text}`}>{h.to}</span>
                    </div>
                    {h.note && <div className="text-xs text-slate-400 mt-1 italic">&ldquo;{h.note}&rdquo;</div>}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      </div>

      {/* Customer message */}
      {sr.customer_message && (
        <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5 mt-6">
          <div className="tick-label text-sky-400 mb-3 flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> Customer Message</div>
          <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap" data-testid="eos-engagement-customer-message">{sr.customer_message}</p>
        </section>
      )}

      {/* Story #8 — Document Generation */}
      <EngagementDocuments engagementId={eng.id} />
    </div>
  );
}

function EngagementDocuments({ engagementId }) {
  const [templates, setTemplates] = useState([]);
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState("sow_v1");
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [openDoc, setOpenDoc] = useState(null);

  const loadTemplates = async () => {
    try { const r = await eosApi().get("/documents/templates"); setTemplates(r.data.templates || []); } catch { /* ignore */ }
  };
  const loadDocs = async () => {
    try { const r = await eosApi().get(`/engagements/${engagementId}/documents`); setDocs(r.data.documents || []); } catch { /* ignore */ }
  };
  useEffect(() => { loadTemplates(); loadDocs(); }, [engagementId]);

  const render = async ({ save = false } = {}) => {
    if (save) setSaving(true); else setRendering(true);
    try {
      const r = await eosApi().post("/documents/render", {
        template_id: selected, engagement_id: engagementId, save,
      });
      setPreview(r.data.content);
      if (save) { toast.success("Document saved"); loadDocs(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Render failed"); }
    finally { setSaving(false); setRendering(false); }
  };

  const openDocument = async (id) => {
    try { const r = await eosApi().get(`/documents/${id}`); setOpenDoc(r.data); }
    catch { toast.error("Could not open document"); }
  };

  const copyContent = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-5 mt-6" data-testid="eos-engagement-documents">
      <div className="tick-label text-sky-400 mb-3 flex items-center gap-1.5"><ClipboardList className="w-3 h-3" /> Documents</div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="flex-1 min-w-[200px]">
          <span className="tick-label mb-1.5 inline-block">Template</span>
          <select
            value={selected} onChange={(e) => setSelected(e.target.value)}
            data-testid="eos-engagement-doc-template"
            className="w-full bg-[var(--bg-primary)] border border-[var(--border-soft)] text-slate-100 text-sm px-3 py-2"
          >
            {templates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </label>
        <button
          onClick={() => render({ save: false })} disabled={rendering}
          data-testid="eos-engagement-doc-preview"
          className="border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-2 disabled:opacity-40"
        >
          {rendering ? "Rendering…" : "Preview"}
        </button>
        <button
          onClick={() => render({ save: true })} disabled={saving}
          data-testid="eos-engagement-doc-generate"
          className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-2 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Generate & Save"}
        </button>
      </div>

      {preview && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="tick-label">Preview</div>
            <button onClick={() => copyContent(preview)} className="font-mono text-[10px] tracking-widest uppercase text-sky-400 hover:text-sky-300">Copy markdown</button>
          </div>
          <pre className="bg-[var(--bg-primary)] border border-[var(--border-soft)] p-4 text-xs text-slate-200 whitespace-pre-wrap font-mono max-h-[400px] overflow-y-auto" data-testid="eos-engagement-doc-preview-body">{preview}</pre>
        </div>
      )}

      {docs.length > 0 && (
        <div>
          <div className="tick-label mb-2">Saved documents ({docs.length})</div>
          <ul className="space-y-2" data-testid="eos-engagement-doc-list">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 border border-[var(--border-soft)] bg-[var(--bg-primary)] p-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-100 truncate">{d.title || d.template_label}</div>
                  <div className="font-mono text-[10px] text-slate-500">{d.template_label} · {new Date(d.created_at).toLocaleString()}</div>
                </div>
                <button onClick={() => openDocument(d.id)} data-testid={`eos-engagement-doc-open-${d.id}`}
                  className="border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[10px] tracking-widest uppercase px-2.5 py-1.5">Open</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {openDoc && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenDoc(null)} data-testid="eos-engagement-doc-modal">
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-soft)] w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-[var(--border-soft)] p-4 flex items-center justify-between">
              <div>
                <div className="tick-label text-sky-400 mb-1">/ {openDoc.template_label}</div>
                <div className="font-display font-bold text-slate-100 truncate max-w-[500px]">{openDoc.title}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => copyContent(openDoc.content)} className="border border-sky-500/60 text-sky-300 font-mono text-[10px] tracking-widest uppercase px-2.5 py-1.5">Copy</button>
                <button onClick={() => setOpenDoc(null)} className="text-slate-400 hover:text-slate-100 text-xl leading-none">×</button>
              </div>
            </div>
            <pre className="bg-[var(--bg-primary)] p-5 text-xs text-slate-200 whitespace-pre-wrap font-mono" data-testid="eos-engagement-doc-modal-body">{openDoc.content}</pre>
          </div>
        </div>
      )}
    </section>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3 py-1 border-b border-[var(--border-soft)] last:border-0">
      <span className="tick-label flex-shrink-0 min-w-[120px]">{label}</span>
      <span className={`text-slate-100 text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}
